import { defineStore } from 'pinia'
import type { ReservationDTO } from '@/modules/reservations/models/reservation'
import type { ReservationUpdateDTO } from '@/modules/reservations/models/reservation-update'
import reservationService from '@/modules/reservations/services/reservation-service'
// import type { ReservationFilters } from '@/modules/reservations/models/reservation-filter'
import { ResponseResult } from '@/core/models/api-responses'
import { ReservationCancelReason } from '../models/reservationCancelReason.enum'

const defaultFilters = {
  pageNumber: 1,
  pageSize: 10,
  orderBy: 'creationDate',
  orderDirection: 'asc',
  employee: '',
  vehicleBrand: '',
  vehicleLicensePlate: '',
  startDate: 0,
  endDate: 0,
  reservationId: '',
  status: ['Open']
}

export const useReservationStore = defineStore('reservation', {
  state: () => ({
    reservations: [] as ReservationDTO[],

    selectedReservation: null as ReservationDTO | null,
    isLoading: false,

    // Pagination data from API
    totalItems: 0,
    totalPages: 0,

    filters: {
      ...defaultFilters
    },
  }),

  getters: {
    // Convenient getters for pagination info
    getCurrentPageInfo: (state) => ({
      pageNumber: state.filters.pageNumber,
      pageSize: state.filters.pageSize,
      totalItems: state.totalItems,
      totalPages: state.totalPages,
      startEntry: (state.filters.pageNumber - 1) * state.filters.pageSize + 1,
      endEntry: Math.min(state.filters.pageNumber * state.filters.pageSize, state.totalItems)
    })
  },

  actions: {

    //Fetch reservations
    async fetchReservations(): Promise<ResponseResult<any>> {

      try {
        this.isLoading = true

        const params = new URLSearchParams()

        if (this.filters.pageNumber !== undefined) params.append('pageNumber', this.filters.pageNumber.toString())
        if (this.filters.pageSize !== undefined) params.append('pageSize', this.filters.pageSize.toString())
        if (this.filters.orderBy) params.append('orderBy', this.filters.orderBy)
        if (this.filters.orderDirection) params.append('orderDirection', this.filters.orderDirection)
        if (this.filters.employee) params.append('employee', this.filters.employee)
        if (this.filters.startDate) params.append('startDate', this.filters.startDate.toString())
        if (this.filters.endDate) params.append('endDate', this.filters.endDate.toString())
        if (this.filters.vehicleBrand) params.append('vehicleBrand', this.filters.vehicleBrand)
        if (this.filters.vehicleLicensePlate) params.append('vehicleLicensePlate', this.filters.vehicleLicensePlate)
        if (this.filters.reservationId) params.append('reservationId', this.filters.reservationId)
        if (this.filters.status?.length) {
          this.filters.status.forEach(status => {
            params.append('reservationStatus', status)
          })
        }

        const queryString = params.toString()
        const res = await reservationService.getAll(queryString)

        if (res.isSuccess && res.data) {
          this.reservations = res.data.items
          this.filters.pageNumber = res.data.pageNumber
          this.filters.pageSize = res.data.pageSize
          this.totalItems = res.data.totalItems
          this.totalPages = res.data.totalPages

        }
        else {
          this.reservations = [];
          this.filters.pageNumber = defaultFilters.pageNumber
          this.filters.pageSize = defaultFilters.pageSize
          this.totalItems = 0
          this.totalPages = 0
        }

        return res;
      }
      catch (error: any) {
        console.error("Erro " + error.response?.status + " erro inesperado")

        this.reservations = [];
        this.filters.pageNumber = defaultFilters.pageNumber
        this.filters.pageSize = defaultFilters.pageSize
        this.totalItems = 0
        this.totalPages = 0

        const response: ResponseResult<null> = {
          isSuccess: false,
          error: error.response?.data?.error?.message ?? "Erro Inesperado",
          timeStamp: new Date().toISOString(),
          statusCode: error.response?.status
        }

        return response;

      }
      finally {
        this.isLoading = false
      }
    },

    //Fetch reservation by id  
    async fetchReservationById(id: string) {
      this.isLoading = true
      this.selectedReservation = null

      try {
        const res = await reservationService.getById(id)

        if (res.isSuccess && res.data) {
          this.selectedReservation = res.data
        }
      } finally {
        this.isLoading = false
      }
    },


    async downloadExcel() {
      try {
        this.isLoading = true

        const params = new URLSearchParams()

        if (this.filters.orderBy) params.append('orderBy', this.filters.orderBy)
        if (this.filters.orderDirection) params.append('orderDirection', this.filters.orderDirection)
        if (this.filters.employee) params.append('employee', this.filters.employee)
        if (this.filters.startDate) params.append('startDate', this.filters.startDate.toString())
        if (this.filters.endDate) params.append('endDate', this.filters.endDate.toString())
        if (this.filters.vehicleBrand) params.append('vehicleBrand', this.filters.vehicleBrand)
        if (this.filters.vehicleLicensePlate) params.append('vehicleLicensePlate', this.filters.vehicleLicensePlate)
        if (this.filters.reservationId) params.append('reservationId', this.filters.reservationId)
        if (this.filters.status?.length) {
          this.filters.status.forEach(status => {
            params.append('reservationStatus', status)
          })
        }

        const queryString = params.toString()

        await reservationService.downloadExcel(queryString)

        // return res;

      } finally {
        this.isLoading = false
      }
      // catch (error: any) {
      //   console.error("Erro " + error.response?.status + " erro inesperado")

      //   this.reservations = [];
      //   this.filters.pageNumber = defaultFilters.pageNumber
      //   this.filters.pageSize = defaultFilters.pageSize
      //   this.totalItems = 0
      //   this.totalPages = 0

      //   const response: ResponseResult<null> = {
      //     isSuccess: false,
      //     error: error.response?.data?.error?.message ?? "Erro Inesperado",
      //     timeStamp: new Date().toISOString(),
      //     statusCode: error.response?.status
      //   }

      //   return response;

      // }
    },

    //Update reservation using the service function
    async updateReservation(id: string, payload: ReservationUpdateDTO): Promise<ResponseResult<any>> {
      try {
        this.isLoading = true

        // Call the service function
        const response = await reservationService.update(id, payload)

        return response

      } finally {
        this.isLoading = false
      }
    },

    // Clear selected reservation (useful when navigating away)
    clearSelectedReservation() {
      this.selectedReservation = null
    },

    // In your store
    async cancelReservation(reservationId: string, cancelationReason: ReservationCancelReason) {
      return await reservationService.cancel(reservationId, cancelationReason)
    }
  },
})